import axios from '@/lib/axios'
import { useQuery } from '@tanstack/vue-query'
import { reactify, useDebounceFn } from '@vueuse/core'
import { reactive, ref, watch } from 'vue'

import { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'
import { PaginatedResponse } from '@/interfaces/paginatedResponse'

import { Brand } from '../interfaces/Brand'
import { Chain } from '../interfaces/Chain'
import { Hotel } from '../interfaces/Hotel'
import { HotelGroups } from '../interfaces/HotelGroup'

interface Props {
	immediate?: boolean
	queryKey?: string
	config?: Config
}

interface Params {
	page: number | string
	filters?: Filters
}

interface Config {
	errorHandling?: boolean
}

interface Filters {
	q?: string
	brandId?: number
	groupId?: number
	chainId?: number
	active?: number | string
	independent?: number
	orderBy?: string
	orderDirection?: string
	hasCode: string | undefined
}

export interface FiltersForm {
	q: string
	brand: Brand | undefined
	group: HotelGroups | undefined
	chain: Chain | undefined
	status?: number
	independent?: number
	orderBy?: string
	orderDirection?: string
	hasCode: string | undefined
}

const getHotels = async (
	params: Params,
	config?: Config
): Promise<PaginatedResponse<Hotel>> => {
	const { errorHandling = false } = config || {}

	console.log(params)

	const { data } = await axios.get<PaginatedResponse<Hotel>>(
		`/api/internal/hotels?page=${params.page}`,
		{
			params: {
				q: params.filters?.q,
				brand_id: params.filters?.brandId,
				group_id: params.filters?.groupId,
				chain_id: params.filters?.chainId,
				state_id:
					params.filters?.active !== 'Todos' ?
						params.filters?.active
					:	undefined,
				independent: params.filters?.independent,
				orderBy: params.filters?.orderBy,
				orderDirection: params.filters?.orderDirection,
				hasCode:
					params.filters?.hasCode !== 'Todos' ?
						params.filters?.hasCode
					:	undefined,
			},
			errorHandling,
		} as CustomAxiosRequestConfig
	)

	return data
}

const useSearchHotels = (props: Props) => {
	const { immediate = true, config } = props

	const page = ref<number | string>(1)

	const filters = reactive<FiltersForm>({
		q: '',
		brand: undefined,
		group: undefined,
		chain: undefined,
		status: undefined,
		independent: undefined,
		orderBy: 'name',
		orderDirection: 'asc',
		hasCode: undefined,
	})

	const { data, isLoading, refetch, isRefetching } = useQuery(
		['hotels', props.queryKey],
		() =>
			getHotels(
				{
					page: page.value,
					filters: {
						q: filters.q,
						brandId: filters.brand?.id,
						groupId: filters.group?.id,
						chainId: filters.chain?.id,
						active: filters.status,
						independent: filters.independent,
						orderBy: filters.orderBy,
						orderDirection: filters.orderDirection,
						hasCode: filters.hasCode,
					},
				},
				{
					errorHandling: config?.errorHandling,
				}
			),
		{
			enabled: immediate,
			refetchOnWindowFocus(query) {
				const now = Date.now()
				const minutes = 10

				const hasPassed = now - query.state.dataUpdatedAt > minutes * 60 * 1000

				return hasPassed
			},
		}
	)

	const setPage = (n: number | string) => {
		page.value = n
	}

	const setQuery = (q: string) => {
		filters.q = q
		page.value = '1'

		console.log('xd')
	}

	const resetFilters = () => {
		filters.q = ''
		filters.brand = undefined
		filters.group = undefined
		filters.chain = undefined
		filters.status = undefined
		filters.independent = undefined
	}

	const debouncedSetQuery = useDebounceFn((e) => {
		setQuery(e)
		page.value = 1
	}, 500)

	watch(filters, () => {
		if (immediate) {
			refetch()
		}
	})

	return {
		data,
		page,
		filters,
		isLoading,
		isRefetching,
		setPage,
		setQuery,
		debouncedSetQuery,
		refetch,

		resetFilters,
	}
}

export default useSearchHotels
